/**
 * EntranceExitDistribution
 *
 * Exibe a distribuição de fluxo por câmera de entrada em dois modos:
 *   - Barras: BarChart horizontal com cores por tipo (entrada/saída).
 *   - Pizza:  PieChart donut com rótulos SVG internos + tabela de ranking.
 *
 * Design decision: `PieItem.pct` (0-100) é mantido separado do campo `percent`
 * que o recharts injeta internamente (0-1) no callback do label. Usar o mesmo
 * nome causaria a multiplicação errada (40 × 100 = 4000%).
 */

import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import type { TooltipContentProps, PieLabelRenderProps } from 'recharts';
import {
  DoorOpen,
  TrendingUp,
  Camera as CameraIcon,
  BarChart3,
  PieChart as PieIcon,
} from 'lucide-react';

// ─── Tipos de domínio ──────────────────────────────────────────────────────────

interface CameraData {
  camera_id: string;
  camera_name: string;
  detections: number;
  people: number;
  gender?: {
    male: number;
    female: number;
    male_percentage: number;
    female_percentage: number;
  };
  age_groups?: {
    child: number;
    teen: number;
    young_adult: number;
    adult: number;
    elderly: number;
  };
}

interface Camera {
  id: string;
  name: string;
  is_entrance?: boolean;
  is_exit?: boolean; // 🚪 Câmera de saída (contagem de pessoas)
  location?: string;
}

interface EntranceExitDistributionProps {
  byCamera?: CameraData[];
  cameras?: Camera[];
  className?: string;
}

// ─── Tipos internos ────────────────────────────────────────────────────────────

type CameraFlowType = 'entrada' | 'saída';

/** Dado processado para cada câmera, passado ao BarChart. */
interface ChartItem {
  name: string;
  people: number;
  detections: number;
  type: CameraFlowType;
  location: string;
  camera_id: string;
  gender?: CameraData['gender'];
  age_groups?: CameraData['age_groups'];
  /** recharts exige index signature para dados do gráfico */
  [key: string]: string | number | CameraData['gender'] | CameraData['age_groups'] | undefined;
}

/**
 * Dado usado pelo PieChart.
 * O campo `pct` é o percentual 0-100 calculado por nós.
 * Não use o nome "percent" — o recharts injeta esse campo (0-1) no callback
 * do label e sobrescreveria o nosso valor.
 */
interface PieItem {
  name: string;
  value: number;
  pct: number;
  location: string;
  gender?: CameraData['gender'];
  age_groups?: CameraData['age_groups'];
  /** recharts exige index signature para dados do gráfico */
  [key: string]: string | number | CameraData['gender'] | CameraData['age_groups'] | undefined;
}

interface CameraStats {
  totalEntradas: number;
  totalSaidas: number;
  maiorFluxo: Pick<ChartItem, 'name' | 'people' | 'type'>;
  menorFluxo: Pick<ChartItem, 'name' | 'people' | 'type'>;
}

// ─── Constantes ────────────────────────────────────────────────────────────────

const PIE_COLORS: readonly string[] = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16',
  '#06b6d4', '#a855f7', '#eab308', '#22c55e', '#0ea5e9',
];

const BAR_COLORS: Record<CameraFlowType, string> = {
  entrada: '#10b981',
  saída:   '#f59e0b',
};

const EMPTY_STATS: CameraStats = {
  totalEntradas: 0,
  totalSaidas:   0,
  maiorFluxo: { name: '', people: 0, type: 'entrada' },
  menorFluxo: { name: '', people: 0, type: 'entrada' },
};

// ─── Helpers de renderização ───────────────────────────────────────────────────

/**
 * Tooltip do BarChart.
 * `TooltipContentProps<number, string>` é o tipo que o recharts espera para
 * um componente de tooltip customizado. O payload[0].payload é ChartItem
 * pois passamos `data={chartData}` diretamente ao BarChart.
 */
const BarTooltip = (
  { active, payload }: TooltipContentProps<number, string>,
): React.ReactElement | null => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload as ChartItem;
  const gender = item.gender;
  const hasGender = gender && (gender.male > 0 || gender.female > 0);
  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
      <p className="font-semibold text-gray-900 dark:text-white mb-2">{item.name}</p>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Tipo: <span className="font-medium">{item.type === 'entrada' ? 'Entrada' : 'Saída'}</span>
      </p>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Local: <span className="font-medium">{item.location}</span>
      </p>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Pessoas:{' '}
        <span className="font-medium text-purple-600 dark:text-purple-400">
          {Number(item.people).toLocaleString('pt-BR')}
        </span>
      </p>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Detecções: <span className="font-medium">{item.detections}</span>
      </p>
      {hasGender && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Gênero</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <span className="text-blue-600 dark:text-blue-400">♂ {gender.male.toLocaleString('pt-BR')} ({gender.male_percentage}%)</span> | <span className="text-pink-500 dark:text-pink-400">♀ {gender.female.toLocaleString('pt-BR')} ({gender.female_percentage}%)</span>
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Rótulo SVG interno de cada fatia do PieChart.
 * O recharts injeta `percent` (0-1) via PieLabelRenderProps — usamos
 * diretamente sem precisar de `any`.
 */
const PieSliceLabel = (props: PieLabelRenderProps): React.ReactElement | null => {
  const numPercent = Number(props.percent ?? 0);
  if (numPercent < 0.04) return null;

  const cx       = Number(props.cx        ?? 0);
  const cy       = Number(props.cy        ?? 0);
  const midAngle = Number(props.midAngle  ?? 0);
  const inner    = Number(props.innerRadius ?? 0);
  const outer    = Number(props.outerRadius ?? 0);

  const RADIAN = Math.PI / 180;
  const radius = inner + (outer - inner) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontSize: 12, fontWeight: 700 }}
    >
      {`${(numPercent * 100).toFixed(1)}%`}
    </text>
  );
};

// ─── Componente principal ──────────────────────────────────────────────────────

export const EntranceExitDistribution: React.FC<EntranceExitDistributionProps> = ({
  byCamera = [],
  cameras  = [],
  className = '',
}) => {
  const [viewMode, setViewMode] = useState<'bar' | 'pie'>('bar');

  const chartData = useMemo((): ChartItem[] => {
    if (!byCamera.length) return [];
    const cameraMap = new Map(cameras.map((c) => [c.id, c]));
    return byCamera
      .map((item): ChartItem => {
        const camera = cameraMap.get(item.camera_id);
        return {
          name:       item.camera_name || 'Câmera sem nome',
          people:     item.people,
          detections: item.detections,
          type:       camera?.is_exit === true ? 'saída' : 'entrada',
          location:   camera?.location ?? 'N/A',
          camera_id:  item.camera_id,
          gender:     item.gender,
          age_groups: item.age_groups,
        };
      })
      .sort((a, b) => b.people - a.people);
  }, [byCamera, cameras]);

  const statistics = useMemo((): CameraStats => {
    if (!chartData.length) return EMPTY_STATS;
    const entradas = chartData.filter((d) => d.type === 'entrada');
    const saidas   = chartData.filter((d) => d.type === 'saída');
    return {
      totalEntradas: entradas.reduce((acc, d) => acc + d.people, 0),
      totalSaidas:   saidas.reduce((acc, d) => acc + d.people, 0),
      maiorFluxo:    chartData[0],
      menorFluxo:    chartData[chartData.length - 1],
    };
  }, [chartData]);

  const pieData = useMemo((): PieItem[] => {
    const total = chartData.reduce((acc, d) => acc + d.people, 0);
    if (total === 0) return [];
    return chartData.map((d) => ({
      name:     d.name,
      value:    d.people,
      pct:      (d.people / total) * 100,
      location: d.location,
      gender:   d.gender,
      age_groups: d.age_groups,
    }));
  }, [chartData]);

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 ${className}`}>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <DoorOpen className="w-6 h-6 text-blue-600" />
            Distribuição por Entradas
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Movimentação por câmera de entrada
          </p>
        </div>

        {/* Toggle Barras / Pizza */}
        <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm overflow-hidden self-start">
          <button
            type="button"
            onClick={() => setViewMode('bar')}
            aria-pressed={viewMode === 'bar'}
            className={`px-3 py-1.5 text-sm font-medium transition inline-flex items-center gap-1.5 ${
              viewMode === 'bar'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Barras
          </button>
          <button
            type="button"
            onClick={() => setViewMode('pie')}
            aria-pressed={viewMode === 'pie'}
            className={`px-3 py-1.5 text-sm font-medium transition inline-flex items-center gap-1.5 border-l border-gray-300 dark:border-gray-600 ${
              viewMode === 'pie'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <PieIcon className="w-4 h-4" />
            Pizza
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <KpiCard
          icon={<DoorOpen className="w-4 h-4" />}
          label="Total de Pessoas"
          value={statistics.totalEntradas.toLocaleString('pt-BR')}
          colorScheme="green"
        />
        <KpiCard
          icon={<TrendingUp className="w-4 h-4" />}
          label="Maior Fluxo"
          value={statistics.maiorFluxo.name || 'N/A'}
          subtitle={`${statistics.maiorFluxo.people.toLocaleString('pt-BR')} pessoas`}
          colorScheme="blue"
        />
        <KpiCard
          icon={<CameraIcon className="w-4 h-4" />}
          label="Total Câmeras"
          value={String(chartData.length)}
          colorScheme="purple"
        />
      </div>

      {/* Gráfico */}
      {chartData.length === 0 ? (
        <EmptyState icon={<CameraIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />} />
      ) : viewMode === 'bar' ? (
        <BarView chartData={chartData} />
      ) : (
        <PieView pieData={pieData} />
      )}

      {/* Legenda */}
      <div className="mt-4 flex flex-wrap gap-4 justify-center">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: BAR_COLORS.entrada }} />
          <span className="text-sm text-gray-600 dark:text-gray-400">Câmeras de Entrada</span>
        </div>
      </div>

      {/* Insight */}
      {chartData.length > 0 && statistics.maiorFluxo.people > 0 && (
        <div className="mt-4 p-4 rounded-lg border bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
          <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
            💡 A câmera <span className="font-bold">{statistics.maiorFluxo.name}</span> tem o maior
            fluxo com{' '}
            <span className="font-bold">{statistics.maiorFluxo.people.toLocaleString('pt-BR')}</span>{' '}
            pessoas. Considere reforçar o monitoramento neste ponto.
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Sub-componentes ───────────────────────────────────────────────────────────

interface KpiCardProps {
  icon: React.ReactElement;
  label: string;
  value: string;
  subtitle?: string;
  colorScheme: 'green' | 'blue' | 'purple';
}

const KpiCard: React.FC<KpiCardProps> = ({ icon, label, value, subtitle, colorScheme }) => {
  const scheme = colorScheme;
  const baseClass = `bg-gradient-to-br rounded-xl p-4 border ${
    scheme === 'green'  ? 'from-green-50  to-green-100  dark:from-green-900/20  dark:to-green-800/20  border-green-200  dark:border-green-700' :
    scheme === 'blue'   ? 'from-blue-50   to-blue-100   dark:from-blue-900/20   dark:to-blue-800/20   border-blue-200   dark:border-blue-700' :
                          'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700'
  }`;
  const labelClass = scheme === 'green'  ? 'text-green-600  dark:text-green-400'  :
                     scheme === 'blue'   ? 'text-blue-600   dark:text-blue-400'   :
                                          'text-purple-600 dark:text-purple-400';
  const valueClass = scheme === 'green'  ? 'text-green-900  dark:text-green-100'  :
                     scheme === 'blue'   ? 'text-blue-900   dark:text-blue-100'   :
                                          'text-purple-900 dark:text-purple-100';

  return (
    <div className={baseClass}>
      <div className={`text-sm font-medium mb-1 flex items-center gap-1 ${labelClass}`}>
        {icon}
        {label}
      </div>
      <div className={`text-2xl font-bold truncate ${valueClass}`}>{value}</div>
      {subtitle && <div className={`text-xs mt-0.5 ${labelClass}`}>{subtitle}</div>}
    </div>
  );
};

const EmptyState: React.FC<{ icon: React.ReactElement }> = ({ icon }) => (
  <div className="h-96 flex items-center justify-center text-gray-500 dark:text-gray-400">
    <div className="text-center">
      {icon}
      <p>Nenhum dado disponível</p>
      <p className="text-sm mt-1">Aguardando detecções das câmeras</p>
    </div>
  </div>
);

const BarView: React.FC<{ chartData: ChartItem[] }> = ({ chartData }) => (
  <div className="h-96">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData} layout="horizontal" margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} angle={-45} textAnchor="end" height={100} />
        <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
        <Tooltip<number, string> content={BarTooltip} />
        <Bar dataKey="people" radius={[8, 8, 0, 0]}>
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={BAR_COLORS[entry.type as CameraFlowType]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const PieView: React.FC<{ pieData: PieItem[] }> = ({ pieData }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center">
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={110}
            paddingAngle={2}
            label={PieSliceLabel}
            labelLine={false}
          >
            {pieData.map((_, index) => (
              <Cell key={`pie-cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip<number, string>
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const pieItem = payload[0]?.payload as PieItem;
              const gender = pieItem.gender;
              const hasGender = gender && (gender.male > 0 || gender.female > 0);
              return (
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 text-sm">
                  <p className="font-semibold text-gray-800 dark:text-gray-100">{pieItem.name}</p>
                  <p className="text-gray-600 dark:text-gray-300">
                    {pieItem.value.toLocaleString('pt-BR')} pessoas ({pieItem.pct.toFixed(1)}%)
                  </p>
                  {hasGender && (
                    <p className="text-gray-500 text-xs mt-1">
                      <span className="text-blue-600 dark:text-blue-400">♂ {gender.male.toLocaleString('pt-BR')} ({gender.male_percentage}%)</span> | <span className="text-pink-500 dark:text-pink-400">♀ {gender.female.toLocaleString('pt-BR')} ({gender.female_percentage}%)</span>
                    </p>
                  )}
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>

    {/* Ranking */}
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      <table className="w-full table-fixed text-sm">
        <thead className="bg-gray-50 dark:bg-gray-700/60">
          <tr>
            <th className="w-10 px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">#</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Câmera</th>
            <th className="w-16 px-2 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Qtd</th>
            <th className="w-14 px-2 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">%</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {pieData.map((p, idx) => (
            <tr key={`rank-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
              <td className="px-2 py-2 text-gray-700 dark:text-gray-200">
                <span className="flex items-center gap-1.5">
                  <span
                    className="inline-block w-3 h-3 rounded-sm shrink-0"
                    style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }}
                  />
                  {idx + 1}
                </span>
              </td>
              <td className="px-2 py-2 text-gray-900 dark:text-gray-100 truncate">{p.name}</td>
              <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{p.value.toLocaleString('pt-BR')}</td>
              <td className="px-2 py-2 text-right font-semibold text-blue-600 dark:text-blue-400">{p.pct.toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
